// Configuración · políticas (docs/108 B10): flujos de APROBACIÓN de venta y de compra, documento por
// canal, crédito y CONDICIONES DE PAGO por canal, y reserva de stock. Una política nunca se edita: se
// registra una versión nueva que reemplaza a la anterior desde su vigencia (queda la historia).
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../api/cliente';
import { useIntentos } from '../api/idempotencia';
import { PanelError } from '../comun/Estados';
import { fechaHora, pesos } from '../comun/formato';
import { Modal } from '../comun/Modal';
import { useSesion } from '../sesion/SesionContexto';

type Fila = Record<string, unknown>;
const t = (v: unknown) =>
  v === null || v === undefined ? '' : Array.isArray(v) ? v.join(', ') : String(v);
const CANALES = ['VENDEDOR', 'CALL_CENTER', 'MESON', 'RUTA', 'B2B', 'POS'];
const CONDICIONES = ['contado', 'cheque_al_dia', 'cheque_a_plazo', 'credito', 'transferencia'];

function useRegistrar(empresaId: string) {
  const qc = useQueryClient();
  const intentos = useIntentos();
  return useMutation<unknown, Error, { ruta: string; cuerpo: unknown }>({
    mutationFn: ({ ruta, cuerpo }) =>
      api.post(ruta, cuerpo, { idempotencia: intentos.clave(`pol:${ruta}`, cuerpo) }),
    onSuccess: async () => {
      intentos.cerrar();
      await qc.invalidateQueries({ queryKey: ['config', 'politicas', empresaId] });
    },
    onError: (e) => intentos.cerrar(e),
  });
}

function Tabla({
  titulo,
  filas,
  columnas,
}: {
  titulo: string;
  filas: Fila[];
  columnas: Array<[string, string, (f: Fila) => string]>;
}) {
  return (
    <>
      <h3 className="titulo-seccion">{titulo}</h3>
      <table className="grilla compacta" aria-label={titulo}>
        <thead>
          <tr>
            {columnas.map(([id, ti]) => (
              <th key={id}>{ti}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {filas.map((f, i) => (
            <tr key={i}>
              {columnas.map(([id, , c]) => (
                <td key={id}>{c(f)}</td>
              ))}
            </tr>
          ))}
          {!filas.length && (
            <tr>
              <td colSpan={columnas.length} className="grilla-vacia">
                Sin políticas
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </>
  );
}

type Nueva = 'aprobacion' | 'canal' | 'credito' | 'reserva' | 'compra';

export function Politicas({ foco }: { foco?: 'condiciones' | 'aprobaciones' | undefined }) {
  const { empresa, puede } = useSesion();
  const empresaId = empresa!.id;
  const ventas = useQuery({
    queryKey: ['config', 'politicas', empresaId, 'ventas'],
    queryFn: () =>
      api.get<{ aprobacion: Fila[]; canal: Fila[]; credito: Fila[]; reserva: Fila[] }>(
        '/ventas/politicas',
        { query: { empresaId } },
      ),
  });
  const compras = useQuery({
    queryKey: ['config', 'politicas', empresaId, 'compras'],
    queryFn: () => api.get<{ versiones: Fila[] }>('/compras/politicas', { query: { empresaId } }),
    enabled: puede('compras.politica.ver'),
  });
  const [nueva, setNueva] = useState<Nueva | null>(null);
  const gestionaVentas = puede('ventas.politica.gestionar');
  const v = ventas.data;
  const vigencia = (f: Fila) =>
    `${fechaHora(t(f['vigente_desde']))}${f['vigente_hasta'] ? ` → ${fechaHora(t(f['vigente_hasta']))}` : ''}`;
  return (
    <div className="pantalla consulta" data-pantalla="politicas">
      <div className="barra-filtros">
        <span className="apagado">
          Cada registro es una versión: la nueva reemplaza a la anterior desde su vigencia.
        </span>
        <span className="espaciador" />
        {gestionaVentas && (
          <>
            <button type="button" onClick={() => setNueva('credito')}>
              Condiciones de pago / crédito
            </button>
            <button type="button" onClick={() => setNueva('aprobacion')}>
              Aprobación de venta
            </button>
            <button type="button" onClick={() => setNueva('canal')}>
              Documento por canal
            </button>
            <button type="button" onClick={() => setNueva('reserva')}>
              Reserva de stock
            </button>
          </>
        )}
        {puede('compras.politica.gestionar') && (
          <button type="button" onClick={() => setNueva('compra')}>
            Aprobación de compra
          </button>
        )}
      </div>
      {ventas.isError && <PanelError error={ventas.error} />}
      {v && (
        <>
          {foco !== 'aprobaciones' && (
            <Tabla
              titulo="Crédito y condiciones de pago por canal"
              filas={v.credito}
              columnas={[
                ['c', 'Canal', (f) => t(f['canal']) || 'todos'],
                ['p', 'Condiciones permitidas', (f) => t(f['condiciones_permitidas'])],
                ['q', 'Con cupo', (f) => t(f['condiciones_con_cupo'])],
                ['m', 'Plazo máx.', (f) => `${t(f['plazo_maximo_dias'])} días`],
                ['d', 'Deuda vencida', (f) => t(f['deuda_vencida']).toLowerCase()],
                ['s', 'Sobrecupo', (f) => t(f['sobrecupo']).toLowerCase()],
              ]}
            />
          )}
          {foco !== 'condiciones' && (
            <Tabla
              titulo="Aprobaciones de venta"
              filas={v.aprobacion}
              columnas={[
                ['c', 'Código', (f) => t(f['codigo'])],
                ['a', 'Ámbito', (f) => t(f['ambito']).toLowerCase().replace(/_/g, ' ')],
                ['r', 'Requiere firma', (f) => (f['requiere_aprobacion'] ? 'sí' : 'no')],
                ['s', 'Otra persona', (f) => (f['exige_segregacion'] ? 'sí' : 'no')],
                ['k', 'Condición', (f) => JSON.stringify(f['condicion'] ?? {})],
                ['v', 'Vigencia', vigencia],
              ]}
            />
          )}
          {!foco && (
            <>
              <Tabla
                titulo="Documento por canal"
                filas={v.canal}
                columnas={[
                  ['c', 'Canal', (f) => t(f['canal'])],
                  [
                    'd',
                    'Documento por defecto',
                    (f) => t(f['documento_por_defecto']).toLowerCase(),
                  ],
                ]}
              />
              <Tabla
                titulo="Reserva de stock por canal"
                filas={v.reserva}
                columnas={[
                  ['c', 'Canal', (f) => t(f['canal'])],
                  ['m', 'Modo', (f) => t(f['modo']).toLowerCase().replace(/_/g, ' ')],
                  [
                    'e',
                    'Expira',
                    (f) => (f['expira_horas'] ? `${t(f['expira_horas'])} h` : 'no expira'),
                  ],
                ]}
              />
            </>
          )}
        </>
      )}
      {foco !== 'condiciones' && compras.data && (
        <Tabla
          titulo="Aprobaciones de compra"
          filas={compras.data.versiones}
          columnas={[
            ['c', 'Código', (f) => t(f['codigo'])],
            ['a', 'Ámbito', (f) => t(f['ambito']).toLowerCase().replace(/_/g, ' ')],
            ['r', 'Requiere firma', (f) => (f['requiere_aprobacion'] ? 'sí' : 'no')],
            ['k', 'Condición', (f) => JSON.stringify(f['condicion'] ?? {})],
            ['v', 'Activa', (f) => (f['activa'] ? 'sí' : 'no')],
          ]}
        />
      )}
      {nueva && <NuevaPolitica tipo={nueva} alCerrar={() => setNueva(null)} />}
    </div>
  );
}

function Opciones({
  id,
  etiqueta,
  opciones,
  valor,
  alCambiar,
}: {
  id: string;
  etiqueta: string;
  opciones: string[];
  valor: string;
  alCambiar: (v: string) => void;
}) {
  return (
    <label className="campo" key={id}>
      <span className="etiqueta">{etiqueta}</span>
      <select aria-label={etiqueta} value={valor} onChange={(e) => alCambiar(e.target.value)}>
        {opciones.map((o) => (
          <option key={o} value={o}>
            {o ? o.toLowerCase().replace(/_/g, ' ') : 'todos'}
          </option>
        ))}
      </select>
    </label>
  );
}

function NuevaPolitica({ tipo, alCerrar }: { tipo: Nueva; alCerrar: () => void }) {
  const { empresa } = useSesion();
  const empresaId = empresa!.id;
  const registrar = useRegistrar(empresaId);
  const [v, setV] = useState<Record<string, string>>({
    canal: tipo === 'credito' ? '' : 'VENDEDOR',
    documento: 'FACTURA',
    modo: 'RESERVA_PARCIAL_CON_PENDIENTE',
    ambito: tipo === 'compra' ? 'ORDEN_COMPRA' : 'NOTA_VENTA',
    deuda: 'AVISAR',
    sobrecupo: 'AUTORIZAR',
    plazo: '30',
    prioridad: '100',
  });
  const [conds, setConds] = useState<Set<string>>(new Set(['contado', 'transferencia']));
  const [conCupo, setConCupo] = useState<Set<string>>(new Set());
  const set = (k: string) => (x: string) => setV((s) => ({ ...s, [k]: x }));
  const texto = (k: string, etiqueta: string) => (
    <label className="campo">
      <span className="etiqueta">{etiqueta}</span>
      <input aria-label={etiqueta} value={v[k] ?? ''} onChange={(e) => set(k)(e.target.value)} />
    </label>
  );
  const motivo = (v['motivo'] ?? '').trim();
  const descripcion = (v['descripcion'] ?? '').trim();
  const cuerpo = (): { ruta: string; cuerpo: unknown } => {
    const monto = (k: string) => (v[k]?.trim() ? { [k]: v[k]!.replace(/\D/g, '') } : {});
    switch (tipo) {
      case 'canal':
        return {
          ruta: '/ventas/politicas/canal',
          cuerpo: {
            datos: {
              empresaId,
              canal: v['canal'],
              documentoPorDefecto: v['documento'],
              descripcion,
            },
            motivo,
          },
        };
      case 'reserva':
        return {
          ruta: '/ventas/politicas/reserva',
          cuerpo: {
            datos: {
              empresaId,
              canal: v['canal'],
              modo: v['modo'],
              expiraHoras: v['expira']?.trim() ? Number(v['expira']) : null,
              descripcion,
            },
            motivo,
          },
        };
      case 'credito':
        return {
          ruta: '/ventas/politicas/credito',
          cuerpo: {
            datos: {
              empresaId,
              ...(v['canal'] ? { canal: v['canal'] } : {}),
              condicionesPermitidas: [...conds],
              condicionesConCupo: [...conCupo].filter((c) => conds.has(c)),
              plazoMaximoDias: Number(v['plazo'] ?? 0),
              deudaVencida: v['deuda'],
              sobrecupo: v['sobrecupo'],
              descripcion,
            },
            motivo,
          },
        };
      case 'aprobacion':
        return {
          ruta: '/ventas/politicas/aprobacion',
          cuerpo: {
            datos: {
              empresaId,
              codigo: (v['codigo'] ?? '').toUpperCase(),
              ambito: v['ambito'],
              prioridad: Number(v['prioridad'] ?? 100),
              requiereAprobacion: true,
              condicion: { ...monto('montoDesde'), ...monto('montoHasta') },
              descripcion,
            },
            motivo,
          },
        };
      case 'compra':
        return {
          ruta: '/compras/politicas',
          cuerpo: {
            politica: {
              empresaId,
              codigo: (v['codigo'] ?? '').toUpperCase(),
              ambito: 'ORDEN_COMPRA',
              prioridad: Number(v['prioridad'] ?? 100),
              requiereAprobacion: true,
              condicion: {
                ...(v['montoDesde']?.trim()
                  ? { montoNetoDesde: v['montoDesde'].replace(/\D/g, '') }
                  : {}),
                ...(v['montoHasta']?.trim()
                  ? { montoNetoHasta: v['montoHasta'].replace(/\D/g, '') }
                  : {}),
              },
              descripcion,
            },
            motivo,
          },
        };
    }
  };
  const valido =
    motivo.length >= 3 &&
    descripcion.length >= 3 &&
    ((tipo !== 'aprobacion' && tipo !== 'compra') ||
      /^[A-Z0-9_]{2,40}$/i.test(v['codigo'] ?? '')) &&
    (tipo !== 'credito' || conds.size > 0);
  const TITULOS: Record<Nueva, string> = {
    aprobacion: 'Nueva aprobación de venta',
    canal: 'Documento por defecto de un canal',
    credito: 'Crédito y condiciones de pago',
    reserva: 'Reserva de stock de un canal',
    compra: 'Nueva aprobación de compra (OC)',
  };
  return (
    <Modal titulo={TITULOS[tipo]} alCerrar={alCerrar}>
      {(tipo === 'canal' || tipo === 'reserva' || tipo === 'credito') && (
        <Opciones
          id="canal"
          etiqueta="Canal"
          opciones={tipo === 'credito' ? ['', ...CANALES] : CANALES}
          valor={v['canal'] ?? ''}
          alCambiar={set('canal')}
        />
      )}
      {tipo === 'canal' && (
        <Opciones
          id="doc"
          etiqueta="Documento por defecto"
          opciones={['FACTURA', 'BOLETA']}
          valor={v['documento']!}
          alCambiar={set('documento')}
        />
      )}
      {tipo === 'reserva' && (
        <>
          <Opciones
            id="modo"
            etiqueta="Modo"
            opciones={['RESERVA_PARCIAL_CON_PENDIENTE', 'RESERVA_TOTAL_OBLIGATORIA']}
            valor={v['modo']!}
            alCambiar={set('modo')}
          />
          {texto('expira', 'Expira en horas (vacío = no expira)')}
        </>
      )}
      {tipo === 'credito' && (
        <>
          <fieldset>
            <legend>Condiciones de pago permitidas (y cuáles consumen cupo)</legend>
            {CONDICIONES.map((c) => (
              <div key={c}>
                <label>
                  <input
                    type="checkbox"
                    aria-label={`Permitir ${c}`}
                    checked={conds.has(c)}
                    onChange={() =>
                      setConds((s) => {
                        const n = new Set(s);
                        if (n.has(c)) n.delete(c);
                        else n.add(c);
                        return n;
                      })
                    }
                  />{' '}
                  {c.replace(/_/g, ' ')}
                </label>{' '}
                <label className="apagado">
                  <input
                    type="checkbox"
                    aria-label={`Cupo ${c}`}
                    disabled={!conds.has(c)}
                    checked={conCupo.has(c)}
                    onChange={() =>
                      setConCupo((s) => {
                        const n = new Set(s);
                        if (n.has(c)) n.delete(c);
                        else n.add(c);
                        return n;
                      })
                    }
                  />{' '}
                  usa cupo
                </label>
              </div>
            ))}
          </fieldset>
          {texto('plazo', 'Plazo máximo (días)')}
          <Opciones
            id="deuda"
            etiqueta="Con deuda vencida"
            opciones={['BLOQUEAR', 'AUTORIZAR', 'AVISAR']}
            valor={v['deuda']!}
            alCambiar={set('deuda')}
          />
          <Opciones
            id="sobrecupo"
            etiqueta="Sobre el cupo"
            opciones={['RECHAZAR', 'AUTORIZAR']}
            valor={v['sobrecupo']!}
            alCambiar={set('sobrecupo')}
          />
        </>
      )}
      {(tipo === 'aprobacion' || tipo === 'compra') && (
        <>
          {texto('codigo', 'Código (MAYÚSCULAS)')}
          {tipo === 'aprobacion' && (
            <Opciones
              id="ambito"
              etiqueta="Ámbito"
              opciones={[
                'NOTA_VENTA',
                'DESCUENTO',
                'PRECIO_MANUAL',
                'CREDITO',
                'FLETE_VENTA',
                'DEVOLUCION_CLIENTE',
              ]}
              valor={v['ambito']!}
              alCambiar={set('ambito')}
            />
          )}
          {texto('montoDesde', tipo === 'compra' ? 'Monto neto desde ($)' : 'Monto desde ($)')}
          {texto(
            'montoHasta',
            tipo === 'compra'
              ? 'Monto neto hasta ($, vacío = sin tope)'
              : 'Monto hasta ($, vacío = sin tope)',
          )}
          {texto('prioridad', 'Prioridad (1 = primero)')}
          <p className="apagado">
            Exige la firma de OTRA persona. Ej.: {pesos('1000000')} o más → requiere aprobación.
          </p>
        </>
      )}
      {texto('descripcion', 'Descripción')}
      {texto('motivo', 'Motivo')}
      {registrar.isError && <PanelError error={registrar.error} />}
      <div className="acciones-modal">
        <button type="button" onClick={alCerrar}>
          Volver
        </button>
        <button
          type="button"
          className="primario"
          disabled={!valido || registrar.isPending}
          onClick={() => registrar.mutate(cuerpo(), { onSuccess: alCerrar })}
        >
          Registrar versión
        </button>
      </div>
    </Modal>
  );
}
